import * as React from 'react'
import { ActivityIndicator, Text, View, ScrollView } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import Picker from '@components/picker/Picker'
import SegmentedControl from '@components/segmentedControl/SegmentedControl'
import Animators from '@components/root/Animators'
import SimpleAlert from '@components/modal/SimpleAlert'

import useI18n from '@i18n/useI18n'

import api from '@api/api'

import { FormikHelpers, useFormik } from 'formik'

interface Values {
  alleeCommande: string
  standCommande: string
  espaceCommande: string
  enseigneCommande: string
}

interface CommandesIncidentFormProps extends IncidentFormProps {
  commandes: CommandeNonConnecteResponse[]
}

const TABS = ['name', 'location']

const LocationSearch = ({ form, onForm, commandes }: CommandesIncidentFormProps) => {
  const init: Values = React.useMemo(
    () => ({
      standCommande: form.standCommande,
      alleeCommande: form.alleeCommande,
      espaceCommande: form.espaceCommande,
      enseigneCommande: form.enseigneCommande,
    }),
    [form.standCommande, form.alleeCommande, form.espaceCommande, form.enseigneCommande]
  )

  const i18n = useI18n()

  const onSubmit = (vals: Values, { setSubmitting }: FormikHelpers<Values>) => {
    onForm(vals).finally(() => setSubmitting(false))
  }

  const { values, setFieldValue, handleSubmit, setValues, dirty } = useFormik<Values>({
    enableReinitialize: true,
    initialValues: init,
    onSubmit,
  })

  const onSpace = (next: string) => {
    setValues({
      ...values,
      espaceCommande: next,
      alleeCommande: '',
      standCommande: '',
    })
  }

  const onLocation = (next: string) => {
    setValues({
      ...values,
      alleeCommande: next,
      standCommande: '',
    })
  }

  const onStand = (next: string) => {
    const selected = commandes.find((c) => c.stand === next)

    setFieldValue('standCommande', next)

    if (selected) {
      SimpleAlert.open({
        title: i18n.t('screens.incident.COMMAND_SEARCH.location'),
        description: i18n.t('screens.incident.COMMAND_SEARCH.validate', { name: selected.enseigne }),
        buttons: [
          {
            label: i18n.t('common.continue'),
            onClick: () => {
              setValues({
                ...values,
                enseigneCommande: selected.enseigne,
                espaceCommande: selected.espace,
                alleeCommande: selected.allee || '',
                standCommande: selected.stand || '',
              })

              SimpleAlert.close()
            },
          },
          {
            label: i18n.t('common.cancel'),
            onClick: () => {
              setFieldValue('standCommande', '')
              SimpleAlert.close()
            },
            color: 'cancel',
          },
        ],
      })
    }
  }

  React.useEffect(() => {
    if (dirty) {
      // sumbit on change
      handleSubmit()
    }
  }, [values])

  const espaces = React.useMemo(
    () => Array.from(new Set(commandes.map((c) => c.espace))).sort((a, b) => a.localeCompare(b)),
    [commandes]
  )

  const allees = React.useMemo(
    () =>
      Array.from(
        new Set(
          // filter on parent if selected
          commandes
            .filter((c) => !values.espaceCommande || c.espace === values.espaceCommande)
            .map((c) => c.allee)
            .filter(Boolean) as string[]
        )
      ).sort((a, b) => a.localeCompare(b)),
    [commandes, values.espaceCommande]
  )
  const stands = React.useMemo(
    () =>
      Array.from(
        // filter on parent if selected
        new Set(
          commandes
            .filter((c) => !values.alleeCommande || c.allee === values.alleeCommande)
            .map((c) => c.stand)
            .filter(Boolean) as string[]
        )
      ).sort((a, b) => a.localeCompare(b)),
    [commandes, values.alleeCommande]
  )

  const spaceOptions = espaces.map((p) => ({ label: p, value: p }))
  const spaceOpt = spaceOptions.find((o) => values.espaceCommande === o.value)

  const locOptions = allees.map((p) => ({ label: p, value: p }))
  const locOpt = locOptions.find((o) => values.alleeCommande === o.value)

  const subOptions = stands.map((p) => ({ label: p, value: p }))
  const subOpt = subOptions.find((o) => values.standCommande === o.value)

  return (
    <Animators>
      <Container>
        {/* position inverted to satisfy picker */}
        {/* stand */}
        <Picker
          key={values.standCommande}
          options={subOptions}
          value={subOpt}
          onChange={(next) => onStand(next ? next.value : '')}
          placeholder={i18n.t('screens.incident.COMMAND_SEARCH.placeholder')}
          disabled={subOptions.length === 0}
          isSearchable
        />
        <Title disabled={subOptions.length === 0}>{i18n.t('screens.incident.COMMAND_SEARCH.subLocation')}</Title>
        {/* allee */}
        <Picker
          options={locOptions}
          value={locOpt}
          onChange={(next) => onLocation(next ? next.value : '')}
          placeholder={i18n.t('screens.incident.COMMAND_SEARCH.placeholder')}
          disabled={locOptions.length === 0}
          isSearchable
        />
        <Title disabled={locOptions.length === 0}>{i18n.t('screens.incident.COMMAND_SEARCH.location')}</Title>
        {/* space */}
        <Picker
          options={spaceOptions}
          value={spaceOpt}
          onChange={(next) => onSpace(next ? next.value : '')}
          placeholder={i18n.t('screens.incident.COMMAND_SEARCH.placeholder')}
          isSearchable
        />
        <Title>{i18n.t('screens.incident.COMMAND_SEARCH.space')}</Title>
      </Container>
    </Animators>
  )
}

const TenantSearch = ({ form, onForm, commandes }: CommandesIncidentFormProps) => {
  const init: Values = React.useMemo(
    () => ({
      standCommande: form.standCommande,
      alleeCommande: form.alleeCommande,
      espaceCommande: form.espaceCommande,
      enseigneCommande: form.enseigneCommande,
    }),
    [form.standCommande, form.alleeCommande, form.espaceCommande, form.enseigneCommande]
  )

  const enseignes = Array.from(new Set(commandes.map((c) => c.enseigne))).sort((a, b) => a.localeCompare(b))

  const i18n = useI18n()

  const onSubmit = (vals: Values, { setSubmitting }: FormikHelpers<Values>) => {
    onForm(vals).finally(() => setSubmitting(false))
  }

  const { values, setFieldValue, handleSubmit, setValues, dirty } = useFormik<Values>({
    enableReinitialize: true,
    initialValues: init,
    onSubmit,
  })

  React.useEffect(() => {
    if (dirty) {
      // sumbit on change
      handleSubmit()
    }
  }, [values])

  const onItemSelected = (next: string) => {
    const selected = commandes.find((c) => c.enseigne === next)

    if (selected) {
      setValues({
        ...values,
        enseigneCommande: selected.enseigne,
        espaceCommande: selected.espace,
        alleeCommande: selected.allee || '',
        standCommande: selected.stand || '',
      })
    } else {
      setFieldValue('enseigneCommande', next)
    }
  }

  const options = enseignes.map((p) => ({ label: p, value: p }))
  const opt = options.find((o) => values.enseigneCommande === o.value)

  return (
    <Animators>
      <Container>
        {/* tenant */}
        <Picker
          options={options}
          value={opt}
          onChange={(next) => onItemSelected(next ? next.value : '')}
          placeholder={i18n.t('screens.incident.COMMAND_SEARCH.placeholder')}
          disabled={options.length === 0}
          isSearchable
        />
        <Title disabled={options.length === 0}>{i18n.t('screens.incident.COMMAND_SEARCH.tenant')}</Title>
      </Container>
    </Animators>
  )
}

const CommandSearch = ({ form, onForm }: IncidentFormProps) => {
  const i18n = useI18n()

  const currentTab = form.commandSearch || 'name'

  const data = TABS.map((t) => ({ value: t, label: i18n.t(`screens.incident.COMMAND_SEARCH.${t}`) }))
  const selected = data.find((d) => d.value === currentTab)

  const [status, setStatus] = React.useState<'loading' | 'loaded' | 'error'>('loaded')
  const [commandes, setCommandes] = React.useState<CommandeNonConnecteResponse[]>([])

  React.useEffect(() => {
    if (form.siteId && form.manifestationId) {
      setStatus('loading')

      api.commande
        .expo(form.siteId, form.manifestationId)
        .then((cmds) => setCommandes(cmds.commandes))
        .then(() => setStatus('loaded'))
        .catch(() => setStatus('error'))
    }
  }, [form.siteId, form.manifestationId, form.isCommandeOrganisateur])

  if (status === 'error') {
    return (
      <CenteredContainer>
        <Animators>
          <Status>{i18n.t('screens.incident.COMMAND.error')}</Status>
        </Animators>
      </CenteredContainer>
    )
  }

  if (status === 'loading') {
    return (
      <CenteredContainer>
        <Animators>
          <ActivityIndicator color={theme.colors.primary} size="large" />
          <Status>{i18n.t('screens.incident.COMMAND.loading')}</Status>
        </Animators>
      </CenteredContainer>
    )
  }

  return (
    <MainContainer>
      <Animators>
        <SegmentedControl data={data} value={selected} onItemSelected={(val) => onForm({ commandSearch: val.value })} />
        {currentTab === 'name' ? (
          <TenantSearch form={form} onForm={onForm} commandes={commandes} />
        ) : (
          <LocationSearch form={form} onForm={onForm} commandes={commandes} />
        )}
      </Animators>
    </MainContainer>
  )
}

export default CommandSearch

const MainContainer = styled(ScrollView)`
  margin: 0 20px 20px;
  padding-top: ${(props) => (props.theme.breakpoint === 'phone' ? 0 : 50)}px;
  flex: 1;
`

const CenteredContainer = styled(View)`
  flex: 1;
  align-items: center;
  justify-content: center;
`

const Container = styled(View)`
  flex-direction: column-reverse;
`

const Title = styled(Text)<{ disabled?: boolean }>`
  ${(props) => props.theme.fonts.Headline};
  opacity: ${(props) => (props.disabled ? 0.2 : 1)};
  margin-bottom: 5px;
  margin-top: 25px;
`

const Status = styled(Text)`
  ${(props) => props.theme.fonts.Option};
  color: ${(props) => props.theme.colors.secondaryText};
  text-align: center;
  margin: 15px;
`
